import { UntypedFormControl, ValidatorFn, UntypedFormGroup } from '@angular/forms';

export class GlobalValidators {
    static validDateRange(beginDateControlName: string, endDateControlName: string): ValidatorFn {
        return (formGroup: UntypedFormGroup): { [key: string]: any } | null => {
            const beginControl = formGroup.controls[beginDateControlName];
            const endControl = formGroup.controls[endDateControlName];
            const beginDate = GlobalValidators.parseDate(beginControl.value);
            const endDate = GlobalValidators.parseDate(endControl.value);

            if (!GlobalValidators.isValidDate(beginDate) || !GlobalValidators.isValidDate(endDate)) return null;

            if (GlobalValidators.compareByDay(beginDate, endDate) >= 0) {
                endControl.setErrors({ dateRange: false });
                return { dateRange: false };
            }

            return null;
        };
    }

    static dateBetween(beginDate: Date, endDate: Date, controlDateFormat: string): ValidatorFn {
        return (control: UntypedFormControl): { [key: string]: any } | null => {
            if (!control.value) return null;

            const controlDate = GlobalValidators.parseDate(control.value, controlDateFormat);

            if (
                !GlobalValidators.isValidDate(controlDate) ||
                !GlobalValidators.isValidDate(beginDate) ||
                !GlobalValidators.isValidDate(endDate)
            ) {
                return null;
            }

            const minDate = GlobalValidators.addDays(beginDate, -1);
            const maxDate = GlobalValidators.addDays(endDate, 1);

            if (!(controlDate > minDate && controlDate < maxDate)) {
                return { dateBetween: false };
            }

            return null;
        };
    }

    static dateRangeMinEndDaysAfter(beginDateControlName: string, endDateControlName: string, daysAfterBeginDate: number): ValidatorFn {
        return (formGroup: UntypedFormGroup): { [key: string]: any } | null => {
            const beginControl = formGroup.controls[beginDateControlName];
            const endControl = formGroup.controls[endDateControlName];
            const beginDate = GlobalValidators.parseDate(beginControl.value);
            const endDate = GlobalValidators.parseDate(endControl.value);

            if (!GlobalValidators.isValidDate(beginDate) || !GlobalValidators.isValidDate(endDate)) return null;

            const minEndDate = GlobalValidators.addDays(beginDate, daysAfterBeginDate);

            if (GlobalValidators.compareByDay(endDate, minEndDate) < 0) {
                endControl.setErrors({ dateRangeMinEndDaysAfter: false });
                return { dateRangeMinEndDaysAfter: false };
            }

            return null;
        };
    }

    static dateRangeMaxEndYearsAfter(beginDateControlName: string, endDateControlName: string, yearsAfterBeginDate: number): ValidatorFn {
        return (formGroup: UntypedFormGroup): { [key: string]: any } | null => {
            const beginControl = formGroup.controls[beginDateControlName];
            const endControl = formGroup.controls[endDateControlName];
            const beginDate = GlobalValidators.parseDate(beginControl.value);
            const endDate = GlobalValidators.parseDate(endControl.value);

            if (!GlobalValidators.isValidDate(beginDate) || !GlobalValidators.isValidDate(endDate)) return null;

            const maxEndDate = GlobalValidators.addYears(beginDate, yearsAfterBeginDate);

            if (GlobalValidators.compareByDay(endDate, maxEndDate) > 0) {
                endControl.setErrors({ dateRangeMaxEndYearsAfter: false });
                return { dateRangeMaxEndYearsAfter: false };
            }

            return null;
        };
    }

    static gradDateMax(control: UntypedFormControl) {
        if (!control.value) return null;

        const today = new Date();
        const controlDate = GlobalValidators.parseDate(control.value);

        if (!GlobalValidators.isValidDate(controlDate) || GlobalValidators.compareByDay(controlDate, today) > 0) {
            return { gradDateMax: false };
        }

        return null;
    }

    static gradDateMin(control: UntypedFormControl) {
        if (!control.value) return null;

        const minPastDate = new Date(2000, 0, 1);
        const controlDate = GlobalValidators.parseDate(control.value);

        if (!GlobalValidators.isValidDate(controlDate) || GlobalValidators.compareByDay(controlDate, minPastDate) < 0) {
            return { gradDateMin: false };
        }

        return null;
    }

    static minDate(minDate: Date) {
        return function (control: UntypedFormControl) {
            if (!control.value) return null;

            const controlDate = GlobalValidators.parseDate(control.value);

            if (!GlobalValidators.isValidDate(controlDate) || GlobalValidators.compareByDay(controlDate, minDate) < 0) {
                return { minDate: false };
            }

            return null;
        };
    }

    static validDate(dateFormat: string) {
        return function (control: UntypedFormControl) {
            if (!control.value) return null;

            const controlDate = GlobalValidators.parseDate(control.value, dateFormat);

            if (!GlobalValidators.isValidDate(controlDate)) {
                return { validDate: false };
            }

            return null;
        };
    }

    static maxDate(maxDate: Date) {
        return function (control: UntypedFormControl) {
            if (!control.value) return null;

            const controlDate = GlobalValidators.parseDate(control.value);

            if (!GlobalValidators.isValidDate(controlDate) || GlobalValidators.compareByDay(controlDate, maxDate) > 0) {
                return { maxDate: false };
            }

            return null;
        };
    }

    static wholeNumbers(control: UntypedFormControl) {
        const number = Number(control.value);
        if (Number.isNaN(number) === false && (number % 1 !== 0)) {
            return { wholeNumbers: false };
        }

        return null;
    }

    private static parseDate(value: any, format?: string): Date {
        if (value instanceof Date) {
            return new Date(value.getTime());
        }

        if (typeof value === 'string' && format) {
            return GlobalValidators.parseDateByFormat(value, format);
        }

        return new Date(value);
    }

    private static parseDateByFormat(value: string, format: string): Date {
        const formatParts = format.match(/(YYYY|MM|DD)/g);
        const valueParts = value.match(/\d+/g);

        if (!formatParts || !valueParts || formatParts.length !== valueParts.length) {
            return new Date(NaN);
        }

        let year = 0;
        let month = 0;
        let day = 1;

        for (let i = 0; i < formatParts.length; i++) {
            const part = formatParts[i];
            const num = Number(valueParts[i]);

            if (Number.isNaN(num)) {
                return new Date(NaN);
            }

            if (part === 'YYYY') year = num;
            if (part === 'MM') month = num;
            if (part === 'DD') day = num;
        }

        const parsedDate = new Date(year, month - 1, day);

        if (
            parsedDate.getFullYear() !== year ||
            parsedDate.getMonth() !== month - 1 ||
            parsedDate.getDate() !== day
        ) {
            return new Date(NaN);
        }

        return parsedDate;
    }

    private static isValidDate(date: Date): boolean {
        return date instanceof Date && !Number.isNaN(date.getTime());
    }

    private static compareByDay(a: Date, b: Date): number {
        const dateA = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
        const dateB = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();

        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        return 0;
    }

    private static addDays(date: Date, days: number): Date {
        const result = new Date(date.getTime());
        result.setDate(result.getDate() + days);
        return result;
    }

    private static addYears(date: Date, years: number): Date {
        const result = new Date(date.getTime());
        result.setFullYear(result.getFullYear() + years);
        return result;
    }
}